import _, { get, last } from 'lodash';

import { AxiosInstance, AxiosResponse } from 'axios';

import * as cache from '@thi.ng/cache';

class ServiceContainer {
  private presenceService: PresenceService;
  private customerService: CustomerService;
  private accountService: AccountService;
  private saleHistoryService: SaleHistoryService;
  private wineMetadataService: WineMetadataService;
  private ratingService: RatingService;

  constructor(
    brixTransport: AxiosInstance,
    apiTransport: AxiosInstance,
    store: object,
    winesCacheImplementation: cache.ICache<string, object>,
    cookie: object
  ) {
    this.presenceService = new PresenceService(store, cookie);
    this.accountService = new AccountService(brixTransport, store);
    this.saleHistoryService = new SaleHistoryService(brixTransport, store);
    this.wineMetadataService = new WineMetadataService(
      winesCacheImplementation,
      brixTransport,
      store
    );
    this.ratingService = new RatingService(apiTransport, store);
    this.customerService = new CustomerService(apiTransport, store);
  }
}

const PRESENCE_IDENTIFIED_KEY: string = 'identified';
const PRESENCE_AUTHENTICATED_KEY: string = 'authenticated';
const PRESENCE_CURRENT_PROFILE_ID: string = 'currentProfileId';
const PRESENCE_CUSTOMER_ID: string = 'customerId';

const loadIdentityToken = (store: object) =>
  // @ts-ignore
  store.get(PRESENCE_IDENTIFIED_KEY);

const prepareAuthorizationHeader = (store: object) =>
  // @ts-ignore
  ({
    headers: { Authorization: loadIdentityToken(store) }
  });

const prepareGraphqlAuthorizationHeader = (store: object) =>
  // @ts-ignore
  ({
    headers: { Authorization: `Brix ${loadIdentityToken(store)}` }
  });

class PresenceService {
  private store: object;
  private cookie: object;

  constructor(store: object, cookie: object) {
    this.store = store;
    this.cookie = cookie;
  }

  setIdentified(value: boolean = true): void {
    // @ts-ignore
    this.store.set(PRESENCE_IDENTIFIED_KEY, value);
  }

  setAuthenticated(value: boolean = true): void {
    // @ts-ignore
    this.cookie.save(PRESENCE_AUTHENTICATED_KEY, value);
  }

  isIdentified(): boolean {
    // @ts-ignore
    return !_.isNil(loadIdentityToken(this.store));
  }

  isAuthenticated(): boolean {
    // @ts-ignore
    return !_.isNil(this.cookie.load(PRESENCE_AUTHENTICATED_KEY));
  }

  setCurrentProfileId(profileId: number): void {
    // @ts-ignore
    this.store.set(PRESENCE_CURRENT_PROFILE_ID, profileId);
  }

  setCustomerId(customerId: number): void {
    // @ts-ignore
    this.store.set(PRESENCE_CUSTOMER_ID, customerId);
  }

  clear(): void {
    // @ts-ignore
    this.store.clearAll();
    // @ts-ignore
    this.cookie.remove(PRESENCE_AUTHENTICATED_KEY);
  }

  getCurrentProfileId(): number {
    // @ts-ignore
    return this.store.get(PRESENCE_CURRENT_PROFILE_ID);
  }

  getCustomerId(): number {
    // @ts-ignore
    return this.store.get(PRESENCE_CUSTOMER_ID);
  }
}
class AccountService {
  private transport: AxiosInstance;
  private store: object;

  constructor(transport: AxiosInstance, store: object) {
    this.transport = transport;
    // @ts-ignore
    this.store = store;
  }

  logIn(email: string, password: string): Promise<object> {
    return this.transport.post('/concierge/sessions', {
      email,
      password
    });
  }

  impersonate(token: string): Promise<object> {
    return this.transport.post('/concierge/impersonate', {
      token
    });
  }

  getDetails(): Promise<object> {
    return this.transport.get(
      '/concierge/sessions',
      prepareAuthorizationHeader(this.store)
    );
  }

  resetPassword(email: string): Promise<object> {
    return this.transport.post('/concierge/passwordReset', {
      email
    });
  }

  updatePassword(resetToken: string, password: string): Promise<object> {
    return this.transport.patch(`/concierge/passwordReset/${resetToken}`, {
      password
    });
  }

  changePassword(password: string, currentPassword: string): Promise<object> {
    return this.transport.patch(
      '/concierge/passwordUpdate',
      {
        newPassword: password,
        currentPassword
      },
      prepareAuthorizationHeader(this.store)
    );
  }

  createProfile(data: object): Promise<object> {
    return this.transport.post(
      `/concierge/profile`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }

  updateProfile(profileId: number, data: object): Promise<object> {
    return this.transport.patch(
      `/concierge/profile/${profileId}`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }

  uploadAvatar(profileId: number, file: any): Promise<object> {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('id', profileId.toString());

    return this.transport.patch(
      `/concierge/profile/${profileId}/avatar`,
      formData,
      prepareAuthorizationHeader(this.store)
    );
  }

  createAddress(data: object): Promise<object> {
    return this.transport.post(
      `/concierge/address`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }

  updateAddress(addressId: number, data: object): Promise<object> {
    return this.transport.patch(
      `/concierge/address/${addressId}`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }

  updatePaymentMethod(data: object): Promise<object> {
    return this.transport.post(
      `/concierge/paymentMethod`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }

  lookupExistingProfile(setupToken: string): Promise<object> {
    return this.transport.get(`/concierge/provision/${setupToken}`);
  }

  activate(setupToken: string, data: object): Promise<object> {
    return this.transport.post(`/concierge/provision/${setupToken}`, data);
  }

  submitHelpRequest(data: object): Promise<object> {
    return this.transport.post(
      `/concierge/contact`,
      data,
      prepareAuthorizationHeader(this.store)
    );
  }
}

const PRODUCT_HISTORY_PAGE_SIZE = 250;

interface GetCustomerResult {
  data: {
    getCustomer: {
      productHistory: { id: string; lastPurchased: string }[];
    };
  };
}
class CustomerService {
  private transport: AxiosInstance;
  private store: object;

  constructor(transport: AxiosInstance, store: object) {
    this.transport = transport;
    // @ts-ignore
    this.store = store;
  }

  /** Calls `onPage` with the history so far as each page arrives. */
  async fetch(
    onPage: (
      productHistory: GetCustomerResult['data']['getCustomer']['productHistory']
    ) => void
  ): Promise<GetCustomerResult> {
    const results = await this.fetchPage();
    let page = get(results, 'data.getCustomer.productHistory', []);
    onPage(page);
    // A short page is the last one.
    while (page.length === PRODUCT_HISTORY_PAGE_SIZE) {
      const { lastPurchased, id } = last(page);
      const nextPage = await this.fetchPage({ lastPurchased, productId: id });
      page = get(nextPage, 'data.getCustomer.productHistory', []);
      results.data.getCustomer.productHistory = [
        ...results.data.getCustomer.productHistory,
        ...page
      ];
      onPage(results.data.getCustomer.productHistory);
    }
    return results;
  }

  fetchPage(cursor?: {
    lastPurchased: string;
    productId: string;
  }): Promise<GetCustomerResult> {
    return this.transport.post(
      '',
      {
        variables: { take: PRODUCT_HISTORY_PAGE_SIZE, cursor },
        query: `
query GetCustomer($take: Int,$cursor: ProductHistoryCursor) {
    getCustomer(take: $take, cursor: $cursor) {
        id
        firstName
        lastName
        email
        referralCode
        dateOfBirth
        address
        avatarId
        avatarUrl
        createdAt
        updatedAt
        deleted
        productHistory {
            id
            name
            posProductId
            upc
            lastPurchased
            lastDelivery
            createdAt
            updatedAt
            deleted
            wineDetails {
                varietal
                region
                servingTemp
                see
                smell
                sip
                savor
                rating
                alcohol
                style
                body
                acidity
                description
                price
                vintage
                fruit
                minerality
                earthiness
                oak
                tannins
                florality
                skinContact
                mlf
                pairsWith
                imageUrl
                conciergeUrl
            }
            rating {
                loved
                score
                notes
                createdAt
                updatedAt
            }
        }
    }
}`
      },
      prepareGraphqlAuthorizationHeader(this.store)
    );
  }

  async fetchReferralFactory(): Promise<{
    code: string;
    token: string;
  } | null> {
    const result = await this.transport.post(
      '',
      {
        query: `
query GetReferralFactory {
  getCustomer {
    referralFactory {
      code
      token
    }
  }
}`
      },
      prepareGraphqlAuthorizationHeader(this.store)
    );

    return result.data.getCustomer.referralFactory;
  }
}

class SaleHistoryService {
  private transport: AxiosInstance;
  private store: object;

  constructor(transport: AxiosInstance, store: object) {
    this.transport = transport;
    // @ts-ignore
    this.store = store;
  }

  fetch(): Promise<object> {
    return this.transport.get(
      '/concierge/salesHistory',
      prepareAuthorizationHeader(this.store)
    );
  }
}

class WineMetadataService {
  private transport: AxiosInstance;
  private winesCacheImplementation: cache.ICache<string, object>;
  private store: object;

  constructor(
    winesCacheImplementation: cache.ICache<string, object>,
    transport: AxiosInstance,
    store: object
  ) {
    this.winesCacheImplementation = winesCacheImplementation;
    this.transport = transport;
    // @ts-ignore
    this.store = store;
  }

  public getCachedWines(wineIds: string[]) {
    return _.reduce(
      wineIds,
      (take, wineId: string) => {
        take[wineId] = this.peek(wineId);

        return take;
      },
      {}
    );
  }

  public fetch(wineIds: string): Promise<any> {
    const partiallyLoadedList = this.getCachedWines(wineIds);
    const authHeader = prepareAuthorizationHeader(this.store);

    const itemsThatNeedToBeFetched = _.pickBy(partiallyLoadedList, _.isNil);
    if (_.size(_.keys(itemsThatNeedToBeFetched)) === 0)
      return Promise.resolve(partiallyLoadedList);

    return this.transport
      .get<object[]>(
        '/concierge/wines',
        _.merge(
          {
            params: { wineIds: _.join(_.keys(itemsThatNeedToBeFetched)) }
          },
          authHeader
        )
      )
      .then((response: AxiosResponse<object[]>) => {
        _.each(response, (wine: object) =>
          this.set(wine['itemID'], this.pruneWineAttributes(wine))
        );

        return this.getCachedWines(wineIds);
      });
  }

  public purge(): void {
    this.winesCacheImplementation = this.winesCacheImplementation.empty();
  }

  private pruneWineAttributes(wine: object): object {
    return _.pick(wine, [
      'itemID',
      'description',
      'conciergeUrl',
      'itemType',
      'wineStyle',
      'grapes',
      'region',
      'price',
      'year',
      'see',
      'savor',
      'smell',
      'servingTemperature',
      'servingTemp',
      'sip'
    ]);
  }

  private peek(wineId: string): object | undefined {
    return this.winesCacheImplementation.get(_.toString(wineId));
  }

  private set(wineId: string, data: object) {
    this.winesCacheImplementation.set(_.toString(wineId), data);
  }
}

class RatingService {
  private transport: AxiosInstance;
  private store: object;

  constructor(transport: AxiosInstance, store: object) {
    this.transport = transport;
    // @ts-ignore
    this.store = store;
  }

  public async persist(
    productId: string,
    rating: 'loved' | 'liked' | 'disliked',
    notes?: string
  ) {
    const result = await this.transport.post(
      '',
      {
        query: `
mutation SaveRating($productId: String!, $rating: SaveRatingInput) {
  saveRating(productId: $productId, rating: $rating) {
    loved
    score
    notes
    createdAt
    updatedAt
  }
}`,
        variables: {
          productId,
          rating: {
            rating: rating.toUpperCase(),
            notes: notes || null
          }
        }
      },
      prepareGraphqlAuthorizationHeader(this.store)
    );

    return result.data.saveRating;
  }
}

export const getServiceContainer = (
  brixTransport: AxiosInstance,
  apiTransport: AxiosInstance,
  store: object,
  winesCacheImplementation: cache.ICache<string, object>,
  cookie: object
): ServiceContainer =>
  new ServiceContainer(
    brixTransport,
    apiTransport,
    store,
    winesCacheImplementation,
    cookie
  );
